// src/components/SunPathPanel.jsx
// =====================  WHERE THIS GOES  =====================
// New file. The actual Sun Path tool body. Rendered by
// ClimateAnalysisPanel when the user opens "Sun Path". Talks to the
// 3D scene only through sunPathStore. Computes its own solar readouts
// (no scene needed) so the panel is useful even before a massing exists.
// ============================================================
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MONTHS, DOY21, solarPosition, sunTimes, fmtHM, compass16, heatCss } from '../utils/solar';
import { useSunPath, setResults } from '../utils/sunPathStore';
import { inIndia, utcOffsetFor } from '../utils/siteRegion';

const FACES = [
  { key: 'N', name: 'North' }, { key: 'E', name: 'East' },
  { key: 'S', name: 'South' }, { key: 'W', name: 'West' }, { key: 'R', name: 'Roof' },
];

export default function SunPathPanel({ project }) {
  const { params, results, status, setParams, runAnalysis, activate, deactivate } = useSunPath();
  const animRef = useRef(null);
  const minutesRef = useRef(params.minutes);
  minutesRef.current = params.minutes;
  const [animating, setAnimating] = useState(false);

  // panel open/close -> scene takes over the light + draws only while visible
  useEffect(() => { activate(); return () => deactivate(); }, []); // eslint-disable-line

  // seed lat/lng from the project origin (your projects key it as center*), and the
  // UTC offset from where that is. It used to stay at India's +5.5 for every site, so
  // a New York project's "20:00" sun was really mid-morning there.
  useEffect(() => {
    const lat = project?.centerLat, lng = project?.centerLng;
    if (lat != null && lng != null) setParams({ lat: +lat, lng: +lng, tz: utcOffsetFor(+lat, +lng).tz });
    // eslint-disable-next-line
  }, [project?.id]);

  const live = useMemo(() => {
    const doy = DOY21[params.month];
    const s = solarPosition(params.lat, params.lng, params.tz, doy, params.minutes);
    const t = sunTimes(params.lat, params.lng, params.tz, doy);
    return { ...s, rise: t.rise, set: t.set };
  }, [params.month, params.minutes, params.lat, params.lng, params.tz]);
  const up = live.altitude > 0;

  // INVALIDATE the analysis whenever a parameter that changes the result changes.
  // The heatmap and the per-facade numbers belong to the month they were swept
  // for; showing them under a new month is stale data wearing a fresh label.
  // Clearing forces a deliberate re-Analyze rather than silently recomputing.
  // `minutes` is excluded: it moves the live sun, not the whole-day sweep.
  const firstParams = useRef(true);
  useEffect(() => {
    if (firstParams.current) { firstParams.current = false; return; }
    setResults(null);
    // eslint-disable-next-line
  }, [params.month, params.lat, params.lng, params.tz]);

  const toggleAnim = () => {
    if (animRef.current) { clearInterval(animRef.current); animRef.current = null; setAnimating(false); return; }
    setAnimating(true);
    animRef.current = setInterval(() => setParams({ minutes: (minutesRef.current + 6) % 1440 }), 60);
  };
  useEffect(() => () => { if (animRef.current) clearInterval(animRef.current); }, []);

  // ---- styles (match CostAnalysisPanel tokens) ----
  const card = { background: 'var(--surface-2,#1a2234)', border: '0.5px solid var(--border,#3a4456)', borderRadius: 12, padding: 16, marginBottom: 12 };
  const lblRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 12, marginBottom: 6, color: 'var(--text-secondary,#c3c9d4)' };
  const val = { fontFamily: 'ui-monospace,Menlo,monospace', color: 'var(--text-primary,#e5e7eb)', fontSize: 13 };
  // boxSizing is the fix: width:100% plus padding and a border, with the default
  // content-box, made each of these WIDER than the third of a row it sits in —
  // so three side by side overflowed and clipped their own values. minWidth:0
  // lets a flex child actually shrink; without it the text still wins.
  //
  // --field rather than --surface-1: the card behind these is --surface-1 too,
  // so the box was the colour of its own background and only a half-pixel
  // border said it was a field at all.
  const numIn = {
    width: '100%', minWidth: 0, boxSizing: 'border-box',
    padding: '6px 8px', fontSize: 12.5,
    fontFamily: 'ui-monospace,Menlo,monospace',
    border: '1px solid var(--field-border, #818d9c)', borderRadius: 8,
    background: 'var(--field, #fff)', color: 'var(--field-ink, #111827)',
  };
  // Blue at rest, green while active — matches the Design Analysis button.
  const btn = (a, disabled) => ({
    flex: 1, padding: '8px 10px', fontSize: 12, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', borderRadius: 8,
    border: `0.5px solid ${a ? 'var(--ok,#3ecf8e)' : 'var(--border-accent,#378add)'}`,
    background: a ? 'var(--ok,#3ecf8e)' : 'var(--border-accent,#378add)',
    color: a ? '#01212a' : '#fff', opacity: disabled ? 0.45 : 1,
  });

  const Toggle = ({ k, label }) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer', color: params[k] ? 'var(--text-primary,#e5e7eb)' : 'var(--text-secondary,#8b92a0)' }}>
      <input type="checkbox" checked={!!params[k]} onChange={(e) => setParams({ [k]: e.target.checked })} style={{ accentColor: 'var(--border-accent,#378add)' }} />
      {label}
    </label>
  );

  const max = results ? Math.max(0.001, ...Object.values(results.totals)) : 1;
  const ordered = results ? [...FACES].sort((a, b) => results.totals[b.key] - results.totals[a.key]) : FACES;

  return (
    <div style={{ fontFamily: 'inherit' }}>
      {/* preconditions */}
      {!status.hasMassing && (
        <div style={{ ...card, border: '1px solid var(--border-accent)', color: 'var(--text-secondary,#c3c9d4)', fontSize: 12 }}>
          Build the massing first (left panel → Build) to run Sun Path against it. The sun & sliders still work below.
        </div>
      )}
      {status.hasMassing && !status.hasContext && (
        <div style={{ ...card, border: '1px solid #8a6d1f', background: 'rgba(138,109,31,0.12)', color: '#e6c34d', fontSize: 12 }}>
          Enable <b>Site Context</b> in the 3D view to include obstruction from surrounding buildings — otherwise every facade reads fully lit.
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted,#8b92a0)', margin: '0 2px 12px' }}>
        <span>{MONTHS[params.month]} · {fmtHM(params.minutes)}</span>
        <span>{params.lat.toFixed(2)}°, {params.lng.toFixed(2)}°</span>
      </div>

      {/* time controls */}
      <div style={card}>
        <div style={lblRow}><span>Month</span><span style={val}>{MONTHS[params.month]}</span></div>
        <input type="range" min={0} max={11} step={1} value={params.month} onChange={(e) => setParams({ month: +e.target.value })} style={{ width: '100%', accentColor: '#378add' }} />
        <div style={{ ...lblRow, marginTop: 14 }}><span>Time of day</span><span style={val}>{fmtHM(params.minutes)}</span></div>
        <input type="range" min={0} max={1439} step={10} value={params.minutes} onChange={(e) => setParams({ minutes: +e.target.value })} style={{ width: '100%', accentColor: '#378add' }} />
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button style={btn(animating)} onClick={toggleAnim}>{animating ? '⏸ Pause' : '▶ Animate day'}</button>
        </div>
      </div>

      {/* location + toggles */}
      <div style={card}>
        <div style={{ display: 'flex', gap: 8 }}>
          {[['lat', 'Latitude °'], ['lng', 'Longitude °'], ['tz', 'UTC offset']].map(([k, cap]) => (
            <div key={k} style={{ flex: 1 }}>
              <div style={{ fontSize: 10, color: 'var(--text-muted,#8b92a0)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 4 }}>{cap}</div>
              <input type="number" step={k === 'tz' ? 0.5 : 0.01} value={params[k]} style={numIn} onChange={(e) => setParams({ [k]: parseFloat(e.target.value) || 0 })} />
            </div>
          ))}
        </div>
        {!inIndia(params.lat, params.lng) && (
          <div style={{ fontSize: 11, lineHeight: 1.45, color: 'var(--text-muted,#8b92a0)', marginTop: 8 }}>
            Outside India, the UTC offset is estimated from longitude and ignores daylight
            saving — set the site's local offset if it differs.
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 }}>
          <Toggle k="showRays" label="Sun rays" />
          <Toggle k="showArc" label="Sun path arc" />
          <Toggle k="showShadows" label="Shadows" />
          <Toggle k="showHeat" label="Facade heatmap" />
        </div>
      </div>

      {/* live readout */}
      <div style={card}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div><div style={{ fontSize: 10, color: 'var(--text-muted,#8b92a0)', textTransform: 'uppercase', letterSpacing: '.4px' }}>Altitude</div>
            <div style={{ ...val, fontSize: 16, marginTop: 2 }}>{up ? `${live.altitude.toFixed(1)}°` : 'below horizon'}</div></div>
          <div><div style={{ fontSize: 10, color: 'var(--text-muted,#8b92a0)', textTransform: 'uppercase', letterSpacing: '.4px' }}>Azimuth</div>
            <div style={{ ...val, fontSize: 16, marginTop: 2 }}>{live.azimuth.toFixed(0)}° {compass16(live.azimuth)}</div></div>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary,#c3c9d4)', marginTop: 10 }}>
          Sunrise <b style={{ color: 'var(--text-primary,#e5e7eb)' }}>{fmtHM(live.rise)}</b>{'  ·  '}Sunset <b style={{ color: 'var(--text-primary,#e5e7eb)' }}>{fmtHM(live.set)}</b>
        </div>
        {/* Analyze runs a whole-DAY sweep, so it only makes sense while the
            clock is still — during Animate the time is moving and the result
            would be for a moment already gone. The facade heatmap stays live
            either way; this button adds the day totals + surface temperature. */}
        <button
          style={{ ...btn(!!results, !status.hasMassing || animating), marginTop: 12, width: '100%' }}
          onClick={() => status.hasMassing && !animating && runAnalysis()}
          disabled={!status.hasMassing || animating}
          title={animating ? 'Pause the animation to run a day analysis' : ''}
        >
          {animating ? 'Pause animation to analyze' : results ? '✓ Analyzed — re-run' : 'Analyze day'}
        </button>
        {!results && status.hasMassing && !animating && (
          <div style={{ fontSize: 10.5, color: 'var(--text-muted,#8b92a0)', marginTop: 6, lineHeight: 1.45 }}>
            Facade colours track the sun live. Analyze adds the whole-day totals
            for {MONTHS[params.month]}.
          </div>
        )}
      </div>

      {/* per-facade direct sun */}
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Direct sun by facade</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted,#8b92a0)', marginBottom: 10 }}>hours per day · obstruction included</div>
        {!results && <div style={{ fontSize: 12, color: 'var(--text-secondary,#c3c9d4)' }}>Press <b>Analyze day</b> to compute per-facade direct-sun hours for {MONTHS[params.month]}.</div>}
        {results && ordered.map((F) => {
          const v = results.totals[F.key], frac = v / max;
          return (
            <div key={F.key} style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '6px 0', fontSize: 12 }}>
              <span style={{ width: 44, color: 'var(--text-secondary,#c3c9d4)' }}>{F.name}</span>
              <span style={{ flex: 1, height: 9, background: 'var(--surface-1,#141a28)', borderRadius: 5, overflow: 'hidden' }}>
                <span style={{ display: 'block', height: '100%', width: `${(frac * 100).toFixed(0)}%`, background: heatCss(frac), borderRadius: 5 }} />
              </span>
              <span style={{ ...val, width: 54, textAlign: 'right' }}>{v.toFixed(1)} h</span>
            </div>
          );
        })}
        {results && (
          <div style={{ marginTop: 10, fontSize: 12, lineHeight: 1.5, color: 'var(--text-secondary,#c3c9d4)', background: 'var(--surface-1,#141a28)', border: '0.5px solid var(--border,#3a4456)', borderRadius: 8, padding: '9px 10px' }}>
            In <b>{results.month}</b>, the <b style={{ color: '#ffd24d' }}>{FACES.find((f) => f.key === results.hi)?.name}</b> facade gets the most direct sun ({results.totals[results.hi].toFixed(1)} h/day); the <b style={{ color: '#7fb4f0' }}>{FACES.find((f) => f.key === results.lo)?.name}</b> facade gets the least ({results.totals[results.lo].toFixed(1)} h/day).
          </div>
        )}
      </div>
    </div>
  );
}
